// import { FC, useState } from 'react';
// import { Select, SelectProps, Spin } from 'antd';
// import { SearchOutlined } from '@ant-design/icons';
// import debounce from 'lodash/debounce';
//
// import { getVendors } from 'api/vendor';
// import { SupplierSearchItem } from 'types/SupplierSearchItem';
// import { useRecoilState } from 'recoil';
// import { vendorState } from recoil/atoms';
//
// interface Props extends SelectProps {
//   width?: any;
//   reseller?: boolean;
//   placeholder?: string;
//   limitToVendorDynamics?: boolean;
//   onChange?: (supplier?: SupplierSearchItem) => void;
// }
//
// export const VendorsSearch: FC<Props> = ({ reseller, placeholder, limitToVendorDynamics, onChange, ...rest }) => {
//   const [loading, setLoading] = useState(false);
//   const [{ vendors }, setVendorState] = useRecoilState(vendorState);
//
//   const handleSearch = debounce(async (str: string) => {
//     if (str.length < 2) {
//       return;
//     }
//
//     setLoading(true);
//     const res = await getVendors(str, reseller, limitToVendorDynamics);
//     if (Array.isArray(res)) setVendorState({ vendors: res });
//     setLoading(false);
//   }, 325);
//
//   const handleChange = (id: number) => {
//     // onChange?.(vendors.find((vendor) => vendor.id === +id));
//     const selected = vendors.find((vendor) => vendor.id === +id);
//     setVendorState((prev) => ({ ...prev, selected }));
//     onChange?.(selected);
//   };
//
//   return (
//     <Select<number>
//       {...rest}
//       placeholder={placeholder || 'Search for Supplier'}
//       notFoundContent={loading ? <Spin size="default" /> : null}
//       suffixIcon={<SearchOutlined />}
//       size="large"
//       showSearch
//       allowClear
//       showArrow
//       virtual={false}
//       filterOption={false}
//       onSearch={handleSearch}
//       onChange={handleChange}
//       onClear={() => setVendorState({ vendors: [] })}
//     >
//       {vendors.map((a) => (
//         <Select.Option key={a.id} value={a.id}>
//           {a.name}
//         </Select.Option>
//       ))}
//     </Select>
//   );
// };

import { useEffect, useState, forwardRef, ReactNode, useRef } from 'react';
import { Divider, Empty, Input, InputRef, Select, SelectProps, Spin } from 'antd';
import { EnterOutlined, SearchOutlined } from '@ant-design/icons';
import { RefSelectProps } from 'antd/lib/select';
import { debounce } from 'lodash-es';

import { useVendorApi } from 'api/vendor';

type Supplier = {
  id: number;
  name: ReactNode;
};

interface Props extends SelectProps<number, Supplier> {
  width?: string | number;
  reseller?: boolean;
  limitToVendorDynamics?: boolean;
  defaultOptions?: Supplier[];
  allowCreate?: boolean;
  onCreateNew?: (supplier: { id: number; name: string }) => void;
  onClear?: () => void;
}

export const VendorsSearch = forwardRef<RefSelectProps, Props>(function VendorSearch(
  {
    width = 480,
    reseller,
    options = [],
    defaultOptions = [],
    limitToVendorDynamics,
    allowCreate,
    onCreateNew,
    onClear,
    ...rest
  },
  ref
) {
  const [loading, setLoading] = useState(false);
  const [vendors, setVendors] = useState<Supplier[]>([]);
  const [newVendor, setNewVendor] = useState('');
  const inputRef = useRef<InputRef>(null);
  const [open, setOpen] = useState<boolean | undefined>(undefined);

  // API
  const { getVendors } = useVendorApi();

  useEffect(() => {
    if (options.length) setVendors(options);
  }, [options]);

  const handleSearch = debounce(async (str: string) => {
    if (str.length < 2) return;

    setLoading(true);
    const res = await getVendors(str, reseller || false, limitToVendorDynamics);
    if (Array.isArray(res)) setVendors([...defaultOptions, ...res]);
    setLoading(false);
  }, 325);

  const handleClear = () => {
    setVendors([]);
    onClear && onClear();
  };

  const onAddNewVendorEnter = (e: React.KeyboardEventHandler<HTMLInputElement> | any) => {
    e.preventDefault();
    setVendors((v) => [...v, { name: newVendor, id: -1 }]);
    setNewVendor('');
    if (onCreateNew) {
      onCreateNew({ name: newVendor, id: -1 });
      setOpen(false);
    }
    setTimeout(() => {
      inputRef.current?.focus();
      setOpen(undefined);
    }, 50);
  };

  return (
    <Select<number, Supplier>
      ref={ref}
      placeholder="Search for Supplier"
      size="large"
      style={{ width }}
      allowClear
      showArrow
      showSearch
      open={open}
      virtual={false}
      filterOption={false}
      notFoundContent={loading ? <Spin size="default" /> : <Empty />}
      suffixIcon={<SearchOutlined />}
      {...rest}
      fieldNames={{ label: 'name', value: 'id' }}
      options={vendors}
      onSearch={handleSearch}
      onClear={handleClear}
      dropdownRender={(menu) => (
        <>
          {menu}
          {allowCreate ? (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div style={{ padding: '0 8px 4px' }}>
                <Input
                  placeholder={`Enter ${reseller ? 'reseller' : 'supplier'} if not found`}
                  style={{ width: 250 }}
                  size={rest.size || 'large'}
                  ref={inputRef}
                  value={newVendor}
                  onChange={(e) => setNewVendor(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  onPressEnter={(e) => onAddNewVendorEnter(e)}
                  suffix={<EnterOutlined />}
                />
              </div>
            </>
          ) : null}
        </>
      )}
    />
  );
});
